<template>
  <div>
    <AlertBox v-if="invitationQuota.event?.ticketCreationDeadlineHint && quotaConfig.showDeadlineExpirationHint" class="mb-5" type="info">
      <template #header> {{ t('EmailInvitationQuota.deadline.title') }} </template>
      {{ t('EmailInvitationQuota.deadline.description', { expiry_date: formattedExpiryDate }) }}
    </AlertBox>
    <AlertBox v-if="tickets && tickets.unlockedCount" class="mb-5" type="danger" :icon-path="mdiAlertOctagon">
      <template #header>{{ t('InvitationQuota.CrewQuota.invalidTickets.overview.title') }}</template>
      {{ t('InvitationQuota.CrewQuota.invalidTickets.overview.description') }}
    </AlertBox>
    <ContentCard>
      <div class="my-6">
        <h2 class="inline">{{ title }}</h2>

        <div class="flex flex-row flex-wrap items-center justify-between">
          <InternalQuotaStats :counts="props.counts.countData" :invitation-quota="props.invitationQuota" />

          <div class="mt-8 flex grow-0 flex-row gap-2">
            <DropDownSelect>
              <template #activator="{ open }">
                <button
                  class="btn-primary-color-s"
                  data-testid="iq-create-btn"
                  :disabled="isLimitReached || (isDeadlineReached && quotaConfig.respectDeadline)"
                  @click.prevent="open"
                >
                  <SvgIcon :path="mdiPlusCircleOutline" :size="16" />
                  {{ t('InvitationQuota.CrewQuota.createTicketButton') }}
                </button>
              </template>
              <template #default>
                <ul class="m-0 w-[175px] list-none p-0">
                  <li
                    class="cursor-pointer divide-y divide-solid divide-(--border-color-main) px-2 py-2 text-left hover:bg-surface-elevation-2"
                  >
                    <RouterLink
                      :to="{ name: 'invitationQuotaCreateTicket', query: route.query }"
                      class="flex text-main"
                      data-testid="iq-ticket-link"
                    >
                      {{ t('InvitationQuota.CrewQuota.singleTicketButton') }}
                    </RouterLink>
                  </li>
                  <li
                    v-if="canUploadCsv"
                    class="cursor-pointer divide-y divide-solid divide-(--border-color-main) px-2 py-2 text-left hover:bg-surface-elevation-2"
                  >
                    <RouterLink :to="{ name: 'invitationQuotaCsvUpload' }" class="flex text-main" data-testid="iq-csv-link">
                      {{ t('InvitationQuota.CrewQuota.createCsvButton') }}
                    </RouterLink>
                  </li>
                </ul>
              </template>
            </DropDownSelect>

            <template v-if="quotaConfig.showExportButton">
              <DropDownSelect v-for="format in exportFormats" :key="`export-drop-down-${format}`">
                <template #activator="{ open }">
                  <button class="btn-outline-color-s" @click.prevent="open">
                    <SvgIcon :path="mdiTrayArrowDown" :size="16" class="mr-2" />
                    {{ t(`InvitationQuota.CrewQuota.exportButton.${format}`) }}
                  </button>
                </template>
                <template #default>
                  <ul class="m-0 w-[175px] list-none p-0">
                    <li
                      v-for="filter in exportFilterValues"
                      :key="`filter_${filter}`"
                      class="cursor-pointer divide-y divide-solid divide-(--border-color-main) px-2 py-2 text-left hover:bg-surface-elevation-2"
                    >
                      <a
                        :href="invitationQuotaTicketsDownloadUrl(toPlainId(props.invitationQuota.id) as string, filter, locale, format)"
                        class="flex text-main"
                      >
                        {{ t(`InvitationQuota.CrewQuota.${filter}`) }} ({{
                          props.counts.countData[filter as keyof TicketCounts['countData']]
                        }})
                      </a>
                    </li>
                  </ul>
                </template>
              </DropDownSelect>
            </template>

            <a v-if="showRequestFormButton" :href="ticketClaimUrl" class="btn-outline-color-s" data-testid="iq-request-link">
              <SvgIcon :path="mdiFileDocumentOutline" :size="16" class="mr-2" />
              {{ t('InvitationQuota.CrewQuota.requestFormButton') }}
            </a>
          </div>
        </div>
      </div>
    </ContentCard>

    <ContentCard class="mt-6">
      <TabBar>
        <NavTabBarItem class="relative pr-2" :to="{ query: { tab: 'activated' } }" :is-selected="props.currentActiveTab == 'activated'">
          {{ t('InvitationQuota.CrewQuota.activatedTab') }}
        </NavTabBarItem>
        <NavTabBarItem
          v-if="showRequestedTickets"
          class="relative pr-2"
          :to="{ query: { tab: 'requests' } }"
          :is-selected="props.currentActiveTab == 'requests'"
        >
          {{ t('InvitationQuota.CrewQuota.requestedTab') }}
          <span class="absolute top-3.25 right-0 mr-3 chip-grey">
            {{ counts.countData.requested }}
          </span>
        </NavTabBarItem>
        <NavTabBarItem
          v-if="showDeactivationFeature"
          class="relative pr-2"
          :to="{ query: { tab: 'deactivated' } }"
          :is-selected="props.currentActiveTab == 'deactivated'"
        >
          {{ t('InvitationQuota.CrewQuota.deactivatedTab') }}
        </NavTabBarItem>
      </TabBar>
      <HorizontalDivider class="-mt-0.25" />
      <div class="mt-4 flex flex-row items-center gap-2">
        <div class="inline-flex flex-row gap-2 rounded-sm border border-strong p-2">
          <SvgIcon :path="mdiMagnify" :size="24" class="text-subtle" />
          <input
            v-model="searchQuery"
            type="text"
            class="outline-none"
            :placeholder="String(t('search'))"
            data-testid="tickets-search-field"
          />
        </div>

        <DropDownSelect>
          <template #activator="{ open }">
            <button
              class="flex items-center rounded-sm border border-solid border-strong p-2"
              data-testid="iq-filter-btn"
              @click.prevent="open"
            >
              <SvgIcon :path="mdiFilterVariant" :size="24" class="mr-2 text-subtle" />
              {{ statusFilterLabel }}
            </button>
          </template>

          <template #default="{ close }">
            <ul class="m-0 list-none p-0">
              <li v-for="option in filterOptions" :key="`filter_${option.value}`" class="separate-rows">
                <label class="block cursor-pointer">
                  <input v-model="status" type="radio" :value="option.value" class="peer pointer-events-none absolute opacity-0" />
                  <div class="px-3 py-2 peer-checked:bg-button-secondary-color hover:bg-button-secondary-color" @click="close">
                    {{ option.text }}
                  </div>
                </label>
              </li>
            </ul>
          </template>
        </DropDownSelect>

        <div class="grow"></div>

        <span v-if="selectedTicketIds.length" class="text-body-s-medium" data-testid="selected-tickets-info">
          {{ t('EmailInvitationQuota.inviteesSelected', { count: selectedTicketIds.length }) }}
        </span>
        <InternalQuotaActionButtons
          v-if="tickets"
          :current-active-tab="currentActiveTab"
          :invitation-quota="invitationQuota"
          :is-deadline-reached="isDeadlineReached"
          :is-limit-reached="isLimitReached"
          :tickets="tickets"
          :selected-ticket-ids="selectedTicketIds"
          @change-ticket-selection="handleTicketsSelectionChange"
          @refetch="refetch"
        />
      </div>
      <InternalQuotaTicketsTable
        v-if="tickets"
        v-model="selectedTicketIds"
        :current-active-tab="props.currentActiveTab"
        :show-status="showStatus"
        :selected-ticket-ids="selectedTicketIds"
        :tickets="tickets.nodes"
        @refetch="refetch"
        @change-ticket-selection="handleTicketsSelectionChange"
      />

      <div v-if="hasTickets" class="flex w-full items-center justify-center gap-2 py-4">
        <PaginationControl
          class="my-4"
          :show-items-per-page="true"
          :per-page="props.perPage"
          :total="tickets?.totalCount"
          show-pagination="always"
          :show-total="true"
          :page-info="tickets?.pageInfo"
          @update:pagination-query-variables="emit('update:paginationQueryVariables', $event)"
          @update:per-page="emit('update:perPage', $event)"
        />
      </div>
    </ContentCard>

    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { mdiPlusCircleOutline, mdiAlertOctagon, mdiMagnify, mdiFilterVariant, mdiTrayArrowDown, mdiFileDocumentOutline } from '@mdi/js'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import type { PaginationQueryVariables } from '@/components'
import { ticketShopBaseUrl } from '@/config/env'
import { type TicketsQuery, type InvitationQuotaQuery } from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import { invitationQuotaTicketsDownloadUrl, exportFormats } from '@/services/ApiService'

import type { TabEnum } from './activeTab'
import getFilterOptions, { type FilterStatusOption } from './filters'
import useQuotaConfig, { type InternalQuotaTypeEnum } from './quotaConfig'
import type { TicketCounts } from './ticketCounts'

const props = defineProps<{
  counts: TicketCounts
  currentActiveTab: TabEnum
  filterStatus: FilterStatusOption
  invitationQuota: InvitationQuotaQuery['invitationQuota']
  isLimitReached: boolean
  perPage: number
  searchQuery?: string
  tickets?: TicketsQuery['tickets']
}>()

const { locale, t } = useI18n()
const route = useRoute()
const showStatus = computed(() => props.currentActiveTab == 'activated')

const quotaConfig = useQuotaConfig(props.invitationQuota.kind as InternalQuotaTypeEnum)

const showRequestedTickets = computed(() => props.invitationQuota.showFormLink)

const canUploadCsv = computed(() => quotaConfig.value.hasCsvUpload)

const showDeactivationFeature = computed(() => quotaConfig.value.showDeactivationFeature)

const isDeadlineReached = computed(() => {
  if (!props.invitationQuota.expirationDate) return false
  return new Date(props.invitationQuota.expirationDate).getTime() < new Date().getTime()
})
const formattedExpiryDate = computed(
  () => new Date(props.invitationQuota.event?.ticketCreationDeadlineHint as string).toLocaleDateString('de-DE') || null,
)

const exportFilterValues = computed(() => {
  const exportValues = ['activated', 'requested']
  return showDeactivationFeature.value ? [...exportValues, 'deactivated'] : exportValues
})

const ticketClaimUrl = computed(() => {
  return `${ticketShopBaseUrl}/claim/${toPlainId(props.invitationQuota.id)}`
})

function handleTicketsSelectionChange(newValue: string[]) {
  selectedTicketIds.value = newValue
}

const emit = defineEmits<{
  (e: 'refetch', value: boolean): void
  (e: 'update:paginationQueryVariables', value: PaginationQueryVariables): void
  (e: 'update:perPage', value: number): void
  (e: 'update:searchQuery', value: string | undefined): void
  (e: 'update:filterStatus', value: FilterStatusOption): void
}>()

const searchQuery = computed({
  get: () => props.searchQuery,
  set: (newValue) => emit('update:searchQuery', newValue),
})

const refetch = (showSuccessModal = false) => emit('refetch', showSuccessModal)

const selectedTicketIds = ref<string[]>([])

const filterOptions = getFilterOptions()
const status = computed({
  get: () => props.filterStatus,
  set: (newValue) => emit('update:filterStatus', newValue),
})
const statusFilterLabel = computed(() => filterOptions.value.find((option) => option.value == props.filterStatus)?.text)

watch(
  () => props.tickets,
  () => {
    selectedTicketIds.value = []
  },
)

const title = computed(() => {
  return props.invitationQuota.title || t('EmailInvitationQuota.defaultTitle')
})

const hasTickets = computed(() => props.tickets?.nodes?.length)

const showRequestFormButton = computed(() => props.invitationQuota.showFormLink)
</script>
